import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  KeyboardEvent,
} from "react";
import { useAuth } from "../contexts/AuthContext";
import { IdentityService } from "../services/identity";
import { DatabaseService } from "../services/database";
import { universities } from "../data/universities";
import { majors } from "../data/majors";
import { X, ArrowLeft, ArrowRight } from "lucide-react";

type StepKey = "about" | "experience" | "motivation" | "confirm";

const stepOrder: StepKey[] = ["about", "experience", "motivation", "confirm"];

const stepTitles: Record<StepKey, string> = {
  about: "About You",
  experience: "Experience",
  motivation: "Motivation",
  confirm: "Confirm",
};

const statusOptions = [
  { value: "in_college", label: "In College" },
  { value: "graduated", label: "Graduated" },
  { value: "other", label: "Other" },
];

interface ReviewerOnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const ReviewerOnboardingModal: React.FC<ReviewerOnboardingModalProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const { user, refreshProfile } = useAuth();
  const [currentStep, setCurrentStep] = useState<StepKey>("about");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [educationLevel, setEducationLevel] = useState<string>("bachelors");

  // Autocomplete states
  const [universityQuery, setUniversityQuery] = useState("");
  const [majorQuery, setMajorQuery] = useState("");
  const [showUniversitySuggestions, setShowUniversitySuggestions] =
    useState(false);
  const [showMajorSuggestions, setShowMajorSuggestions] = useState(false);
  const [universitySelectedIndex, setUniversitySelectedIndex] = useState(-1);
  const [majorSelectedIndex, setMajorSelectedIndex] = useState(-1);

  // Refs for handling clicks outside suggestion boxes and scrolling
  const universityRef = useRef<HTMLDivElement>(null);
  const majorRef = useRef<HTMLDivElement>(null);
  const universityListRef = useRef<HTMLDivElement>(null);
  const majorListRef = useRef<HTMLDivElement>(null);
  const universityInputRef = useRef<HTMLInputElement>(null);
  const majorInputRef = useRef<HTMLInputElement>(null);

  // Reset modal state when it opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep("about");
      setAnswers({});
      setEducationLevel("bachelors");
      setUniversityQuery("");
      setMajorQuery("");
    }
  }, [isOpen]);

  // Prefill from pre-answers captured in modal
  const prefillAnswers = () => {
    try {
      const raw = localStorage.getItem("reviewerPreAnswers");
      if (raw) {
        const parsed = JSON.parse(raw);
        setAnswers((prev) => ({ ...prev, ...parsed }));
        if (parsed.university) setUniversityQuery(parsed.university);
        if (parsed.major) setMajorQuery(parsed.major);
        if (parsed.education_level) setEducationLevel(parsed.education_level);
        localStorage.removeItem("reviewerPreAnswers"); // Clear once used
      }
    } catch (_) {}
  };

  useEffect(() => {
    if (isOpen) {
      prefillAnswers();
    }

    // Add click outside listener for autocomplete dropdowns
    const handleClickOutside = (event: MouseEvent) => {
      if (
        universityRef.current &&
        !universityRef.current.contains(event.target as Node)
      ) {
        setShowUniversitySuggestions(false);
      }
      if (
        majorRef.current &&
        !majorRef.current.contains(event.target as Node)
      ) {
        setShowMajorSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (universitySelectedIndex >= 0 && universityListRef.current) {
      const items = universityListRef.current.querySelectorAll("div");
      if (items[universitySelectedIndex]) {
        items[universitySelectedIndex].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [universitySelectedIndex]);

  useEffect(() => {
    if (majorSelectedIndex >= 0 && majorListRef.current) {
      const items = majorListRef.current.querySelectorAll("div");
      if (items[majorSelectedIndex]) {
        items[majorSelectedIndex].scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [majorSelectedIndex]);

  const progressPercent = useMemo(() => {
    const idx = stepOrder.indexOf(currentStep);
    return Math.round(((idx + 1) / stepOrder.length) * 100);
  }, [currentStep]);

  const goNext = useCallback(async () => {
    const idx = stepOrder.indexOf(currentStep);
    if (idx < stepOrder.length - 1) {
      setCurrentStep(stepOrder[idx + 1]);
    } else {
      // Done
      try {
        // Mark user as onboarded and add reviewer role
        if (user?.id) {
          await DatabaseService.markUserAsOnboarded(user.id);
          await IdentityService.addRole(user.id, "reviewer");
          // Update user profile with education level
          await DatabaseService.updateUser(user.id, {
            education_level: educationLevel,
            major: majorQuery || answers.major,
            university: universityQuery || answers.university,
            bio: answers.motivation,
            status: answers.status as any,
          });

          // Add reviewer role and create reviewer profile
          await DatabaseService.addReviewerRole(user.id);

          // Update role tags based on status (student/alum)
          if (answers.status) {
            await DatabaseService.updateRoleTagsBasedOnStatus(
              user.id,
              answers.status as any
            );
          }

          // Create or update reviewer profile with additional data
          await DatabaseService.createReviewerProfile(user.id, {
            motivation: answers.motivation,
            years_of_experience: answers.experience
              ? parseInt(answers.experience) || 0
              : 0,
          });

          // Refresh profile cache to show updated data
          await refreshProfile();
        }
      } catch (error) {
        console.error("Failed to complete reviewer onboarding:", error);
      }

      // Close modal and notify completion
      onClose();
      onComplete?.();
    }
  }, [
    currentStep,
    user?.id,
    educationLevel,
    majorQuery,
    universityQuery,
    answers,
    onClose,
    onComplete,
    refreshProfile,
  ]);

  const goBack = useCallback(() => {
    const idx = stepOrder.indexOf(currentStep);
    if (idx > 0) setCurrentStep(stepOrder[idx - 1]);
    else onClose(); // Close modal if on the first step
  }, [currentStep, onClose]);

  // Filtered suggestions
  const filteredUniversities = useMemo(() => {
    return universities
      .filter((uni) =>
        uni.toLowerCase().includes(universityQuery.toLowerCase())
      )
      .slice(0, 8); // Show 8 suggestions
  }, [universityQuery]);

  const filteredMajors = useMemo(() => {
    return majors
      .filter((major) => major.toLowerCase().includes(majorQuery.toLowerCase()))
      .slice(0, 8); // Show 8 suggestions
  }, [majorQuery]);

  // Handle university input and selection
  const handleUniversityChange = useCallback((value: string) => {
    setUniversityQuery(value);
    setAnswers((prev) => ({ ...prev, university: value }));
    setShowUniversitySuggestions(true);
    setUniversitySelectedIndex(-1); // Reset selection when typing
  }, []);

  const selectUniversity = useCallback((university: string) => {
    setUniversityQuery(university);
    setAnswers((prev) => ({ ...prev, university }));
    setShowUniversitySuggestions(false);
    setUniversitySelectedIndex(-1);
  }, []);

  // Handle major input and selection
  const handleMajorChange = useCallback((value: string) => {
    setMajorQuery(value);
    setAnswers((prev) => ({ ...prev, major: value }));
    setShowMajorSuggestions(true);
    setMajorSelectedIndex(-1); // Reset selection when typing
  }, []);

  const selectMajor = useCallback((major: string) => {
    setMajorQuery(major);
    setAnswers((prev) => ({ ...prev, major }));
    setShowMajorSuggestions(false);
    setMajorSelectedIndex(-1);
  }, []);

  // Handle keyboard navigation for university field
  const handleUniversityKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!showUniversitySuggestions) {
        if (e.key === "ArrowDown" && filteredUniversities.length > 0) {
          setShowUniversitySuggestions(true);
          return;
        }
      }

      if (filteredUniversities.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setUniversitySelectedIndex((prev) =>
            prev < filteredUniversities.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setUniversitySelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredUniversities.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (universitySelectedIndex >= 0) {
            selectUniversity(filteredUniversities[universitySelectedIndex]);
          } else if (filteredUniversities.length > 0) {
            // If no selection but Enter pressed, select first suggestion
            selectUniversity(filteredUniversities[0]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowUniversitySuggestions(false);
          setUniversitySelectedIndex(-1);
          break;
      }
    },
    [
      showUniversitySuggestions,
      filteredUniversities,
      universitySelectedIndex,
      selectUniversity,
    ]
  );

  // Handle keyboard navigation for major field
  const handleMajorKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (!showMajorSuggestions) {
        if (e.key === "ArrowDown" && filteredMajors.length > 0) {
          setShowMajorSuggestions(true);
          return;
        }
      }

      if (filteredMajors.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setMajorSelectedIndex((prev) =>
            prev < filteredMajors.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setMajorSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredMajors.length - 1
          );
          break;
        case "Enter":
          e.preventDefault();
          if (majorSelectedIndex >= 0) {
            selectMajor(filteredMajors[majorSelectedIndex]);
          } else if (filteredMajors.length > 0) {
            // If no selection but Enter pressed, select first suggestion
            selectMajor(filteredMajors[0]);
          }
          break;
        case "Escape":
          e.preventDefault();
          setShowMajorSuggestions(false);
          setMajorSelectedIndex(-1);
          break;
      }
    },
    [showMajorSuggestions, filteredMajors, majorSelectedIndex, selectMajor]
  );

  const handleStatusChange = useCallback((v: string) => {
    setAnswers((a) => ({ ...a, status: v }));
  }, []);

  const handleExperienceChange = useCallback((v: string) => {
    setAnswers((a) => ({ ...a, experience: v }));
  }, []);

  const handleMotivationChange = useCallback((v: string) => {
    setAnswers((a) => ({ ...a, motivation: v }));
  }, []);

  // Autocomplete input field
  const AutocompleteField = useCallback(
    ({
      label,
      value,
      onChange,
      placeholder,
      suggestions,
      showSuggestions,
      onSelectSuggestion,
      inputRef,
      listRef,
      selectedIndex,
      onKeyDown,
    }: {
      label: string;
      value: string;
      onChange: (v: string) => void;
      placeholder?: string;
      suggestions: string[];
      showSuggestions: boolean;
      onSelectSuggestion: (suggestion: string) => void;
      inputRef: React.RefObject<HTMLInputElement>;
      listRef: React.RefObject<HTMLDivElement>;
      selectedIndex: number;
      onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    }) => (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <div className="relative">
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => suggestions.length > 0 && onChange(value)}
            onKeyDown={(e) => {
              // Handle tab key to immediately close dropdown
              if (e.key === "Tab") {
                if (showSuggestions) {
                  if (label.includes("University")) {
                    setShowUniversitySuggestions(false);
                  } else if (label.includes("Major")) {
                    setShowMajorSuggestions(false);
                  }
                }
              }
              // Call the original onKeyDown handler
              onKeyDown(e);
            }}
            onBlur={(e) => {
              // For mouse interactions, we still need a small delay
              // to allow clicks on suggestions to register
              if (
                !e.relatedTarget ||
                (e.relatedTarget &&
                  !listRef.current?.contains(e.relatedTarget as Node))
              ) {
                if (showSuggestions) {
                  // Close dropdown when clicking elsewhere
                  if (label.includes("University")) {
                    setShowUniversitySuggestions(false);
                  } else if (label.includes("Major")) {
                    setShowMajorSuggestions(false);
                  }
                }
              }
            }}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
            placeholder={placeholder}
          />
        </div>
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={listRef}
            className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion}
                className={`px-4 py-2 cursor-pointer ${
                  selectedIndex === index ? "bg-gray-100" : "hover:bg-gray-50"
                } relative`}
                onClick={() => onSelectSuggestion(suggestion)}
              >
                {suggestion}
                {index === 0 && (
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-400">
                    Press Enter ↵
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    [setShowUniversitySuggestions, setShowMajorSuggestions]
  );

  // Regular input field
  const Field = useCallback(
    ({
      label,
      value,
      onChange,
      placeholder,
    }: {
      label: string;
      value: string;
      onChange: (v: string) => void;
      placeholder?: string;
    }) => (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
          placeholder={placeholder}
        />
      </div>
    ),
    []
  );

  const TextArea = useCallback(
    ({
      label,
      value,
      onChange,
      placeholder,
    }: {
      label: string;
      value: string;
      onChange: (v: string) => void;
      placeholder?: string;
    }) => (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
          rows={5}
          placeholder={placeholder}
        />
      </div>
    ),
    []
  );

  // Create a ref outside the callback
  const selectRef = useRef<HTMLSelectElement>(null);

  const SelectField = useCallback(
    ({
      label,
      value,
      onChange,
      options,
    }: {
      label: string;
      value: string;
      onChange: (v: string) => void;
      options: { value: string; label: string }[];
    }) => {
      const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onChange(e.target.value);
        // Blur the select element after selection to remove focus
        setTimeout(() => {
          if (selectRef.current) {
            selectRef.current.blur();
          }
        }, 0);
      };

      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
          <select
            ref={selectRef}
            value={value}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="">Select an option</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    },
    []
  );

  // Memoize the steps to prevent re-renders
  const aboutStep = useMemo(
    () => (
      <div className="space-y-4">
        <div ref={universityRef}>
          <AutocompleteField
            label="University"
            value={universityQuery}
            onChange={handleUniversityChange}
            placeholder="e.g., Harvard University"
            suggestions={filteredUniversities}
            showSuggestions={showUniversitySuggestions}
            onSelectSuggestion={selectUniversity}
            inputRef={universityInputRef}
            listRef={universityListRef}
            selectedIndex={universitySelectedIndex}
            onKeyDown={handleUniversityKeyDown}
          />
        </div>
        <div ref={majorRef}>
          <AutocompleteField
            label="Major or Field"
            value={majorQuery}
            onChange={handleMajorChange}
            placeholder="e.g., Computer Science"
            suggestions={filteredMajors}
            showSuggestions={showMajorSuggestions}
            onSelectSuggestion={selectMajor}
            inputRef={majorInputRef}
            listRef={majorListRef}
            selectedIndex={majorSelectedIndex}
            onKeyDown={handleMajorKeyDown}
          />
        </div>
        <SelectField
          label="Current Status"
          value={answers.status || ""}
          onChange={handleStatusChange}
          options={statusOptions}
        />
        <SelectField
          label="Highest Education Level"
          value={educationLevel}
          onChange={(v) => setEducationLevel(v)}
          options={[
            { value: "high_school", label: "High School" },
            { value: "bachelors", label: "Bachelor's Degree" },
            { value: "masters", label: "Master's Degree" },
            { value: "phd", label: "PhD or Doctorate" },
            { value: "other", label: "Other" },
          ]}
        />
      </div>
    ),
    [
      universityQuery,
      majorQuery,
      answers.status,
      educationLevel,
      filteredUniversities,
      filteredMajors,
      showUniversitySuggestions,
      showMajorSuggestions,
      universitySelectedIndex,
      majorSelectedIndex,
      AutocompleteField,
      SelectField,
      handleUniversityChange,
      handleMajorChange,
      handleStatusChange,
      selectUniversity,
      selectMajor,
      handleUniversityKeyDown,
      handleMajorKeyDown,
    ]
  );

  const experienceStep = useMemo(
    () => (
      <div className="space-y-4">
        <TextArea
          label="Relevant Experience"
          value={answers.experience || ""}
          onChange={handleExperienceChange}
          placeholder="Tutoring, editing, admissions, etc."
        />
      </div>
    ),
    [answers.experience, TextArea, handleExperienceChange]
  );

  const motivationStep = useMemo(
    () => (
      <div className="space-y-4">
        <TextArea
          label="Why do you want to review essays?"
          value={answers.motivation || ""}
          onChange={handleMotivationChange}
          placeholder="Share your motivation"
        />
      </div>
    ),
    [answers.motivation, TextArea, handleMotivationChange]
  );

  const confirmStep = useMemo(() => {
    // Find the status label
    const statusLabel =
      statusOptions.find((opt) => opt.value === answers.status)?.label ||
      answers.status ||
      "—";

    const educationLevelLabel =
      {
        high_school: "High School",
        bachelors: "Bachelor's Degree",
        masters: "Master's Degree",
        phd: "PhD or Doctorate",
        other: "Other",
      }[educationLevel] || "—";

    return (
      <div className="space-y-3 text-gray-700">
        <div className="font-medium">Review your info</div>
        <div>University: {answers.university || universityQuery || "—"}</div>
        <div>Major: {answers.major || majorQuery || "—"}</div>
        <div>Status: {statusLabel}</div>
        <div>Education Level: {educationLevelLabel}</div>
        <div>Experience: {answers.experience || "—"}</div>
        <div>Motivation: {answers.motivation || "—"}</div>
      </div>
    );
  }, [answers, educationLevel, majorQuery, universityQuery]);

  // Render the current step
  const currentStepContent = useMemo(() => {
    switch (currentStep) {
      case "about":
        return aboutStep;
      case "experience":
        return experienceStep;
      case "motivation":
        return motivationStep;
      case "confirm":
        return confirmStep;
      default:
        return null;
    }
  }, [currentStep, aboutStep, experienceStep, motivationStep, confirmStep]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Become a Reviewer
              </h2>
              <p className="text-gray-600 text-sm">
                Complete the steps below to join as a reviewer.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="w-full bg-gray-100 rounded-full h-2 mb-6">
            <div
              className="bg-gray-900 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          <div className="mb-4 text-sm text-gray-600">
            Step {stepOrder.indexOf(currentStep) + 1} of {stepOrder.length}:{" "}
            <span className="font-medium text-gray-900">
              {stepTitles[currentStep]}
            </span>
          </div>

          <div className="border border-gray-200 rounded-xl p-5 mb-6 min-h-[300px]">
            {currentStepContent}
          </div>

          <div className="flex justify-between">
            <button
              onClick={goBack}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {currentStep === "about" ? "Cancel" : "Back"}
            </button>
            <button
              onClick={goNext}
              className="flex items-center gap-2 bg-black text-white px-5 py-2 rounded-lg hover:bg-gray-800 transition-colors"
            >
              {currentStep === "confirm" ? "Complete Setup" : "Continue"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewerOnboardingModal;
